/** Wide modal dialog for editing one integration.
 *
 * Booking → inner Config / Webhooks tabs (schema-driven config editor + webhook
 * panel). Fleet → inner Configuration / Vehicles tabs (the Quartix form + a
 * vehicle roster with List and Map subtabs). The account slug is shown read-only
 * (it is baked into externally-configured webhook URLs); the display name is
 * editable. Delete lives in the footer.
 *
 * A Dialog (zIndex.modal = 1300) is used rather than a Drawer because the app's
 * AppBar sits at `zIndex.drawer + 1` (1201) — a temporary Drawer (zIndex.drawer,
 * 1200) renders *under* it. The wide (`maxWidth="lg"`) layout also gives the
 * config grid the horizontal room its value cells need to be editable.
 */
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import type { IntegrationDetail, IntegrationRow } from '@/api/integrations';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import {
  accountActions,
  createdByActions,
  fleetSystemActions,
  restechActions,
  type TenantCtx,
} from '../tenantLinks';

import { SchemaConfigGrid } from '../config/schema-grid/SchemaConfigGrid';
import { SCHEMA_STALE_MS } from './cachePolicy';
import type { SchemaObject } from '../config/schema-grid/types';
import { integrationWidget } from './config-widgets';
import { FleetFields } from './FleetFields';
import { FleetVehiclesActions } from './FleetVehiclesActions';
import { SubscriptionsPanel } from './SubscriptionsPanel';
import { WebhookPanel } from './WebhookPanel';
import { fleetSettings, type FleetRegion, type FleetWizardData } from './wizard/types';

/** Booking-row inner tabs. `subscriptions` only renders when the restech
 *  supports notification subscriptions (flat capability field). */
type BookingSubtab = 'config' | 'webhooks' | 'subscriptions';

function toFleetData(d: IntegrationDetail): FleetWizardData {
  const s = (d.settings ?? {}) as Record<string, unknown>;
  return {
    region: (s.region as FleetRegion) ?? 'uk',
    customer_id: String(s.customer_id ?? ''),
    username: String(s.username ?? ''),
    password: String(s.password ?? ''),
    application_id: String(s.application_id ?? ''),
    site_name: String(s.site_name ?? ''),
    user: String(s.user ?? ''),
    keep_location_history: Boolean(d.keep_location_history),
    unassign_if_not_in_api: Boolean(d.unassign_if_not_in_api),
    hidden_vehicle_statuses: (d.hidden_vehicle_statuses ?? []) as string[],
    route_unassign_events: (d.route_unassign_events ?? []) as string[],
    enabled: Boolean(d.enabled),
  };
}

interface Props {
  open: boolean;
  toId: number;
  toCode: string;
  row: IntegrationRow | null;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Optional URL segments after the integration id: [dialogTab, view?].
   *  When provided (with onNavigate), the dialog's inner tabs are URL-driven. */
  path?: string[];
  onNavigate?: (sub: string[]) => void;
}

export function IntegrationEditDialog({
  open,
  toId,
  toCode,
  row,
  canManage,
  onClose,
  onSaved,
  path,
  onNavigate,
}: Props) {
  const snackbar = useSnackbar();
  const [subtabState, setSubtabState] = useState<BookingSubtab>('config');
  const [fleetTabState, setFleetTabState] = useState<'config' | 'vehicles'>('config');
  const urlDriven = Boolean(onNavigate);
  const rawSubtab: BookingSubtab = urlDriven
    ? path?.[0] === 'webhooks' || path?.[0] === 'subscriptions'
      ? (path[0] as BookingSubtab)
      : 'config'
    : subtabState;
  const setSubtab = (v: BookingSubtab) =>
    urlDriven ? onNavigate!([v]) : setSubtabState(v);
  const fleetTab: 'config' | 'vehicles' = urlDriven
    ? (path?.[0] === 'vehicles' ? 'vehicles' : 'config')
    : fleetTabState;
  const setFleetTab = (v: 'config' | 'vehicles') =>
    urlDriven ? onNavigate!([v]) : setFleetTabState(v);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detailQuery = useQuery({
    // reveal=canManage → managers get real secrets so the field's eye toggle can
    // show them (view-only users get masks).
    queryKey: ['integrations', 'detail', toId, row?.id, canManage],
    queryFn: () => integrationsApi.retrieve(toId, row!.id, { reveal: canManage }),
    enabled: open && !!row,
  });
  const detail = detailQuery.data;
  const isBooking = row?.kind === 'booking';
  // Flat capability field (same contract level as the webhook flags): gates
  // the Subscriptions tab. A deep-linked `subscriptions` segment on a restech
  // without the capability falls back to the config tab.
  const supportsSubscriptions = Boolean(detail?.can_manage_notification_subscriptions);
  const subtab: BookingSubtab =
    rawSubtab === 'subscriptions' && !supportsSubscriptions ? 'config' : rawSubtab;
  // Fleet row id is `fleet:{pk}`; the vehicle endpoints need the numeric pk.
  const fleetId = row?.kind === 'fleet' ? Number(row.id.replace(/^fleet:/, '')) : null;

  // Captured out here: inside SchemaConfigGrid's renderValue, `row` is the
  // CONFIG row, which shadows this one.
  const restech = (row?.restech as string | undefined) ?? null;

  const ctx: TenantCtx = { id: toId, code: toCode };
  const systemActions = restech
    ? restechActions(restech, ctx)
    : row?.fleet_mgmt
      ? fleetSystemActions(row.fleet_mgmt, ctx)
      : [];

  const schemaQuery = useQuery({
    queryKey: ['integrations', 'schema', row?.restech],
    queryFn: () => integrationsApi.getSchema(row!.restech as string),
    enabled: open && isBooking && !!row?.restech,
    staleTime: SCHEMA_STALE_MS,
  });

  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>({});
  const [displayName, setDisplayName] = useState('');
  const [fleetData, setFleetData] = useState<FleetWizardData | null>(null);
  const [configWarnings, setConfigWarnings] = useState<integrationsApi.SchemaValidationError[]>([]);

  // Field-path → message (+ fix hint) for the config grid to highlight rows.
  // Root-level warnings (empty path) can't map to a field, so they're list-only.
  const warningsByPath = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of configWarnings) {
      if (!e.path) continue;
      const hint = integrationsApi.validationFixHint(e);
      m[e.path] = hint ? `${e.message} — ${hint}` : e.message;
    }
    return m;
  }, [configWarnings]);

  useEffect(() => {
    if (!detail) return;
    if (detail.kind === 'booking') {
      setLocalConfig((detail.raw_config ?? {}) as Record<string, unknown>);
      setDisplayName(detail.display_name ?? '');
    } else {
      setFleetData(toFleetData(detail));
    }
  }, [detail, row?.id]);

  // Reset the sub-tabs + clear stale validation errors when a different row opens.
  // URL-driven mode: the URL owns the tab (deep links open on the right tab and
  // navigating away resets naturally), so only local fallback state is reset.
  useEffect(() => {
    if (!urlDriven) {
      setSubtabState('config');
      setFleetTabState('config');
    }
    setConfigWarnings([]);
  }, [row?.id, urlDriven]);

  const saveMutation = useMutation({
    mutationFn: (body: integrationsApi.IntegrationUpdateInput) =>
      integrationsApi.update(toId, row!.id, body),
    onSuccess: (data) => {
      // Save always succeeds; schema mismatches come back as advisory warnings.
      const w = data?.config_warnings ?? [];
      setConfigWarnings(w);
      snackbar.show(
        w.length
          ? `Saved — ${w.length} field${w.length > 1 ? 's' : ''} don’t match the schema (see warnings).`
          : 'Integration saved.',
        w.length ? 'warning' : 'success',
      );
      void detailQuery.refetch();
      onSaved();
    },
    onError: (e: unknown) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        snackbar.show('This integration changed since you loaded it — reopen and retry.', 'error');
      } else {
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.remove(toId, row!.id),
    onSuccess: () => {
      snackbar.show('Integration deleted.', 'success');
      setConfirmDelete(false);
      onSaved();
      onClose();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  function saveBooking() {
    setConfigWarnings([]); // clear prior warnings; the save re-computes them server-side
    saveMutation.mutate({ config: localConfig, display_name: displayName, replace: true });
  }
  function saveFleet() {
    if (!fleetData) return;
    saveMutation.mutate({
      enabled: fleetData.enabled,
      settings: fleetSettings(fleetData),
      keep_location_history: fleetData.keep_location_history,
      unassign_if_not_in_api: fleetData.unassign_if_not_in_api,
      hidden_vehicle_statuses: fleetData.hidden_vehicle_statuses,
      route_unassign_events: fleetData.route_unassign_events,
    });
  }

  const deleteMessage = row?.is_default
    ? `This clears the default configuration for ${row?.restech ?? 'this system'}.`
    : row?.kind === 'fleet'
      ? 'This permanently removes the fleet tracking configuration.'
      : `This removes the "${row?.slug}" account. Its externally-configured webhook URLs will stop working.`;

  const showBookingSave = isBooking && subtab === 'config' && canManage;
  const showFleetSave = !isBooking && canManage && !!fleetData && fleetTab === 'config';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={row?.kind === 'fleet' ? 'xl' : 'lg'}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="div">
            {row?.display_name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <ValueActions chip actions={systemActions}>
              <Chip
                size="small"
                label={row?.kind === 'fleet' ? row.fleet_mgmt : row?.restech}
                color={row?.kind === 'fleet' ? 'secondary' : 'primary'}
              />
            </ValueActions>
            {isBooking &&
              (row?.is_default || !row?.slug ? (
                <Chip size="small" variant="outlined" label="Default account" />
              ) : (
                <Tooltip title="Account slug — part of the webhook URLs configured in the provider. It cannot be changed.">
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <ValueActions chip actions={accountActions(row.slug, ctx)}>
                      <Chip size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} label={row.slug} />
                    </ValueActions>
                  </Box>
                </Tooltip>
              ))}
            {/* `created_by` / `created_ts` are in the payload and were never
                rendered — who set this integration up is the first question
                asked when one starts misbehaving. */}
            {row?.created_by != null && (
              <Typography variant="caption" color="text.secondary">
                Added by{' '}
                <ValueActions actions={createdByActions(row.created_by)}>
                  user #{row.created_by}
                </ValueActions>
                {row.created_ts && ` · ${new Date(row.created_ts).toLocaleDateString()}`}
              </Typography>
            )}
          </Stack>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {detailQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {detailQuery.error && <Alert severity="error">Couldn’t load the integration.</Alert>}

        {detail && isBooking && (
          <>
            <Tabs value={subtab} onChange={(_, v) => setSubtab(v as BookingSubtab)} sx={{ mb: 2 }}>
              <Tab value="config" label="Configuration" />
              <Tab value="webhooks" label="Webhooks" />
              {supportsSubscriptions && <Tab value="subscriptions" label="Subscriptions" />}
            </Tabs>

            {subtab === 'config' && (
              <Stack spacing={2}>
                {configWarnings.length > 0 && (
                  <Alert severity="warning" onClose={() => setConfigWarnings([])}>
                    <AlertTitle>Saved — some fields don’t match the schema</AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {configWarnings.map((e, i) => {
                        const hint = integrationsApi.validationFixHint(e);
                        return (
                          <Box component="li" key={`${e.path}:${e.keyword}:${i}`} sx={{ mb: 0.25 }}>
                            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {e.path || '(root)'}
                            </Box>
                            {` — ${e.message}`}
                            {hint && <Box component="span" sx={{ color: 'text.secondary' }}>{` (${hint})`}</Box>}
                          </Box>
                        );
                      })}
                    </Box>
                  </Alert>
                )}
                <TextField
                  label="Display name"
                  size="small"
                  value={displayName}
                  disabled={!canManage}
                  onChange={(e) => setDisplayName(e.target.value)}
                  sx={{ maxWidth: 320 }}
                />
                {schemaQuery.isLoading ? (
                  <Typography variant="body2">Loading schema…</Typography>
                ) : (
                  <Box sx={{ height: '58vh', minHeight: 360 }}>
                    <SchemaConfigGrid
                      schema={schemaQuery.data as unknown as SchemaObject | undefined}
                      value={localConfig}
                      onChange={setLocalConfig}
                      hiddenKeyPrefixes={['_cp_']}
                      readOnly={!canManage}
                      warningsByPath={warningsByPath}
                      renderValue={(row, onChange, ro) =>
                        integrationWidget({
                          row,
                          onChange,
                          readOnly: ro,
                          toId,
                          restech,
                        })
                      }
                    />
                  </Box>
                )}
              </Stack>
            )}

            {subtab === 'webhooks' && (
              <WebhookPanel
                toId={toId}
                toCode={toCode}
                integrationId={row.id}
                detail={detail}
                canManage={canManage}
                onChanged={() => void detailQuery.refetch()}
              />
            )}

            {subtab === 'subscriptions' && supportsSubscriptions && row.restech && (
              <SubscriptionsPanel
                toId={toId}
                restech={row.restech}
                slug={row.is_default ? null : row.slug}
                canManage={canManage}
              />
            )}
          </>
        )}

        {detail && !isBooking && fleetData && (
          <>
            <Tabs value={fleetTab} onChange={(_, v) => setFleetTab(v as 'config' | 'vehicles')} sx={{ mb: 2 }}>
              <Tab value="config" label="Configuration" />
              <Tab value="vehicles" label="Vehicles" />
            </Tabs>

            {fleetTab === 'config' && (
              <FleetFields
                value={fleetData}
                onChange={(p) => setFleetData({ ...fleetData, ...p })}
                disabled={!canManage}
              />
            )}

            {fleetTab === 'vehicles' && fleetId !== null && (
              <FleetVehiclesActions toId={toId} fleetId={fleetId} canManage={canManage} />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {canManage && (
          <Button color="error" onClick={() => setConfirmDelete(true)}>
            Delete integration
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
        {showBookingSave && (
          <Button variant="contained" onClick={saveBooking} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        )}
        {showFleetSave && (
          <Button variant="contained" onClick={saveFleet} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </DialogActions>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete integration?"
        message={deleteMessage}
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Dialog>
  );
}
